import { useEffect, useMemo, useState } from 'react';
import { Edit3, Plus, Trash2, Save, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import VendorProfilePicker from './VendorProfilePicker';
import RunningTotalsWidget from './RunningTotalsWidget';
import ApplyCreditPanel from './ApplyCreditPanel';
import { api } from '../../api/client';

function centsFromDollars(s) {
  if (s === '' || s == null) return 0;
  const n = Number(String(s).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
function dollarsFromCents(c) {
  return ((c || 0) / 100).toFixed(2);
}

export default function EditCheckModal({ open, check, onClose, onSaved }) {
  // Check-level fields
  const [checkNumber, setCheckNumber] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [vendorProfileId, setVendorProfileId] = useState(null);
  const [roNumber, setRoNumber] = useState('');

  // Per-invoice editable state
  const [invoices, setInvoices] = useState([]);

  const [activeTab, setActiveTab] = useState('check');  // 'check' or 'inv:<id>'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !check) return;
    setCheckNumber(check.check_number || '');
    setAmountStr(dollarsFromCents(check.amount_cents));
    setVendorProfileId(check.vendor_profile_id || null);
    setRoNumber(check.ro_number_extracted || '');
    setInvoices((check.invoices || []).map(inv => ({
      id: inv.id,
      ro_number: inv.ro_number_extracted || '',
      amount_str: dollarsFromCents(inv.amount_cents),
      vendor_invoice_number: inv.vendor_invoice_number || '',
      bundle_purchase: !!inv.bundle_purchase,
      line_items: parseLineItems(inv.line_items_json),
      _dirty: false,
    })));
    setActiveTab('check');
    setError(null);
  }, [open, check]);

  const checkAmountCents = useMemo(() => centsFromDollars(amountStr), [amountStr]);

  const handleSave = async () => {
    if (!check) return;
    setSaving(true);
    setError(null);
    try {
      // Save invoice edits first (so the rollup at the end uses fresh invoice data)
      for (const inv of invoices) {
        if (!inv._dirty) continue;
        await api.editCodCheckInvoice(inv.id, {
          amount_cents: centsFromDollars(inv.amount_str),
          ro_number: inv.ro_number || null,
          vendor_invoice_number: inv.vendor_invoice_number || null,
          line_items: inv.line_items,
          bundle_purchase: inv.bundle_purchase,
          rerun_audit: true,
        });
      }

      // Then check-level edits
      const checkPayload = {};
      if (checkNumber !== (check.check_number || '')) checkPayload.check_number = checkNumber;
      if (checkAmountCents !== (check.amount_cents || 0)) checkPayload.amount_cents = checkAmountCents;
      if (vendorProfileId !== (check.vendor_profile_id || null)) checkPayload.vendor_profile_id = vendorProfileId;
      if (roNumber !== (check.ro_number_extracted || '')) checkPayload.ro_number = roNumber;

      let result;
      if (Object.keys(checkPayload).length > 0) {
        result = await api.editCodCheck(check.id, { ...checkPayload, rerun_audit: true });
      }

      onSaved?.(result);
      onClose?.();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const updateInvoice = (id, patch) => {
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...patch, _dirty: true } : inv));
  };

  const addLineItem = (invId) => {
    updateInvoice(invId, { line_items: [...(invoices.find(i => i.id === invId)?.line_items || []), { part_number: '', description: '', quantity: 1, unit_price: 0, line_total: 0 }] });
  };

  const updateLineItem = (invId, idx, patch) => {
    const inv = invoices.find(i => i.id === invId);
    if (!inv) return;
    const next = [...inv.line_items];
    next[idx] = { ...next[idx], ...patch };
    // Auto-recalc line_total when quantity or unit_price change
    if ('quantity' in patch || 'unit_price' in patch) {
      const q = Number(next[idx].quantity) || 0;
      const u = Number(next[idx].unit_price) || 0;
      next[idx].line_total = +(q * u).toFixed(2);
    }
    updateInvoice(invId, { line_items: next });
  };

  const removeLineItem = (invId, idx) => {
    const inv = invoices.find(i => i.id === invId);
    if (!inv) return;
    updateInvoice(invId, { line_items: inv.line_items.filter((_, i) => i !== idx) });
  };

  if (!check) return null;

  const activeInv = activeTab.startsWith('inv:')
    ? invoices.find(i => String(i.id) === activeTab.slice(4))
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit · Check #${check.check_number || '—'}`}
      description={`${check.matched_vendor_name || check.payee || 'Unknown vendor'} · ${dollarsFromCents(check.amount_cents)}`}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} className="flex items-center gap-1.5">
            <Save className="w-4 h-4" />
            Save & Re-audit
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-md bg-[var(--color-danger-50)] border border-[var(--color-danger-300)] p-3 text-[13px] text-[var(--color-danger-700)]">
          {error}
        </div>
      )}

      {/* Sub-tabs: Check / each invoice */}
      <div className="flex items-center gap-1 border-b border-[var(--color-warm-200)] mb-4">
        <TabBtn active={activeTab === 'check'} onClick={() => setActiveTab('check')}>
          Check
        </TabBtn>
        {invoices.map(inv => (
          <TabBtn key={inv.id} active={activeTab === `inv:${inv.id}`} onClick={() => setActiveTab(`inv:${inv.id}`)}>
            Invoice p{(check.invoices || []).find(i => i.id === inv.id)?.page_index ?? '—'}
            {inv._dirty && <span className="ml-1 text-[var(--color-action-600)]">●</span>}
          </TabBtn>
        ))}
      </div>

      {activeTab === 'check' && (
        <div className="space-y-4">
          <Field label="Check number">
            <input
              type="text"
              value={checkNumber}
              onChange={e => setCheckNumber(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] font-mono"
            />
          </Field>

          <Field label="Amount ($)">
            <input
              type="text"
              value={amountStr}
              onChange={e => setAmountStr(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] tabular-nums"
            />
          </Field>

          <Field label="RO# / PO# (optional override at check level)">
            <input
              type="text"
              value={roNumber}
              onChange={e => setRoNumber(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] font-mono"
            />
          </Field>

          <Field label="Vendor profile">
            <VendorProfilePicker
              shopId={check.shop_id}
              value={vendorProfileId}
              onChange={setVendorProfileId}
            />
          </Field>

          {/* Apply COD Credit — renders only when check has check_payment_mismatch */}
          <ApplyCreditPanel
            check={check}
            onApplied={(result) => {
              // If the credit closed the mismatch, close the modal + refresh
              if (result?.mismatch_cleared) {
                onSaved?.(result);
                onClose?.();
              } else {
                // Otherwise leave the modal open — there may still be other findings
                onSaved?.(result);
              }
            }}
          />
        </div>
      )}

      {activeInv && (
        <InvoiceEditor
          shopId={check.shop_id}
          invoice={activeInv}
          checkAmountCents={checkAmountCents}
          onChange={patch => updateInvoice(activeInv.id, patch)}
          onAddLine={() => addLineItem(activeInv.id)}
          onUpdateLine={(idx, patch) => updateLineItem(activeInv.id, idx, patch)}
          onRemoveLine={idx => removeLineItem(activeInv.id, idx)}
        />
      )}
    </Modal>
  );
}

function TabBtn({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-[13px] font-semibold border-b-2 transition-colors ${
        active
          ? 'border-[var(--color-warm-800)] text-[var(--color-warm-900)]'
          : 'border-transparent text-[var(--color-warm-500)] hover:text-[var(--color-warm-700)]'
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1">{label}</label>
      {children}
    </div>
  );
}

function parseLineItems(json) {
  if (!json) return [];
  try {
    const arr = JSON.parse(json);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function InvoiceEditor({ shopId, invoice, checkAmountCents, onChange, onAddLine, onUpdateLine, onRemoveLine }) {
  const lineSumCents = useMemo(
    () => Math.round((invoice.line_items || []).reduce((s, li) => s + (Number(li.line_total) || 0), 0) * 100),
    [invoice.line_items]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="RO# / PO#">
          <input
            type="text"
            value={invoice.ro_number}
            onChange={e => onChange({ ro_number: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] font-mono"
          />
        </Field>
        <Field label="Invoice amount ($)">
          <input
            type="text"
            value={invoice.amount_str}
            onChange={e => onChange({ amount_str: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] tabular-nums"
          />
        </Field>
        <Field label="Vendor invoice #">
          <input
            type="text"
            value={invoice.vendor_invoice_number}
            onChange={e => onChange({ vendor_invoice_number: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-[var(--color-warm-200)] focus:outline-none focus:border-[var(--color-warm-500)] text-[14px] font-mono"
          />
        </Field>
        <Field label="Bundle / Kit purchase">
          <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--color-warm-200)] bg-white cursor-pointer hover:border-[var(--color-warm-400)]">
            <input
              type="checkbox"
              checked={invoice.bundle_purchase}
              onChange={e => onChange({ bundle_purchase: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-[13px] text-[var(--color-warm-800)]">
              Verify by total only (skip per-line part match)
            </span>
          </label>
        </Field>
      </div>

      <RunningTotalsWidget
        shopId={shopId}
        roNumber={invoice.ro_number}
        invoiceLineSumCents={lineSumCents}
        checkAmountCents={checkAmountCents}
      />

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[12px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold">Line items</h4>
          <button
            onClick={onAddLine}
            className="text-[12px] font-semibold text-[var(--color-warm-700)] hover:text-[var(--color-warm-900)] flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Add line
          </button>
        </div>
        <div className="rounded-md border border-[var(--color-warm-200)] overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[var(--color-cream-100)] text-[10px] uppercase tracking-wider text-[var(--color-warm-500)] font-bold">
            <div className="col-span-3">Part #</div>
            <div className="col-span-4">Description</div>
            <div className="col-span-1 text-right">Qty</div>
            <div className="col-span-2 text-right">Unit $</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-1"></div>
          </div>
          {(invoice.line_items || []).map((li, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-1.5 border-t border-[var(--color-warm-100)] items-center text-[12px]">
              <input
                value={li.part_number || ''}
                onChange={e => onUpdateLine(idx, { part_number: e.target.value })}
                className="col-span-3 px-2 py-1 rounded border border-[var(--color-warm-200)] font-mono"
              />
              <input
                value={li.description || ''}
                onChange={e => onUpdateLine(idx, { description: e.target.value })}
                className="col-span-4 px-2 py-1 rounded border border-[var(--color-warm-200)]"
              />
              <input
                type="number" step="any" inputMode="decimal"
                value={li.quantity ?? ''}
                onChange={e => onUpdateLine(idx, { quantity: e.target.value })}
                className="col-span-1 px-2 py-1 rounded border border-[var(--color-warm-200)] text-right tabular-nums"
              />
              <input
                type="number" step="any" inputMode="decimal"
                value={li.unit_price ?? ''}
                onChange={e => onUpdateLine(idx, { unit_price: e.target.value })}
                className="col-span-2 px-2 py-1 rounded border border-[var(--color-warm-200)] text-right tabular-nums"
              />
              <input
                type="number" step="any" inputMode="decimal"
                value={li.line_total ?? ''}
                onChange={e => onUpdateLine(idx, { line_total: e.target.value })}
                className="col-span-1 px-2 py-1 rounded border border-[var(--color-warm-200)] text-right tabular-nums"
              />
              <button
                onClick={() => onRemoveLine(idx)}
                className="col-span-1 text-[var(--color-warm-500)] hover:text-[var(--color-danger-600)] justify-self-center"
                title="Remove line"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {(invoice.line_items || []).length === 0 && (
            <div className="px-3 py-4 text-[12px] text-[var(--color-warm-500)] text-center italic">
              No line items.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
